// The explicit things the selected square can be told to do, and the double
// click that picks one for you.
//
// `useSelectionActions` as verbs over `SessionCore`: a shortcut that acts
// closes whatever picker was open, which is why it writes the session's
// fields rather than taking setters.

import { isGameOver } from "src/engine/rules/chess/status";
import { canLoadBelt, canMine, loadTarget } from "src/engine/rules/industry/machines";
import { mineableAt, unitAt } from "src/engine/rules/industry/state";

import type { SessionCore } from "./core";
import { REFUSAL } from "./refusals";
import { actRefusal } from "./turn";

export type SelectionActionKind = "mine" | "load-belt";

/**
 * A free action on the selection.
 *
 * Structural, so the client's own `SelectionAction` takes it straight in.
 * `done` is "you already did this", information rather than a missing button.
 */
export interface SelectionAction {
  kind: SelectionActionKind;
  label: string;
  /** The square the action acts on, when it is not the selected one. */
  square?: number;
  /** Already spent this turn: shown, and switched off. */
  done?: boolean;
}

export function selectionVerbs(core: SessionCore) {
  const actionsFor = (square: number) => {
    const { state, localSide } = core;
    const out: { kind: "mine" | "load-belt"; label: string }[] = [];
    if (canMine(state.position, state.industry, square, localSide) === null) {
      out.push({ kind: "mine", label: "Mine" });
    }
    if (canLoadBelt(state.position, state.industry, square, localSide) === null) {
      out.push({ kind: "load-belt", label: "Hand off" });
    }
    return out;
  };

  /**
   * What the selected square could do right now, as explicit actions. These
   * are the CTAs: mining and handing ore to a belt used to happen silently on
   * the tick, which meant the economy ran without the player ever pressing
   * anything or seeing it happen.
   *
   * Was a `useMemo` over the selection; the view calls it when it rebuilds.
   */
  const actions = (): SelectionAction[] => {
    const { state, localSide, selected } = core;
    if (selected === undefined) return [];
    const out: SelectionAction[] = [];
    const why = canMine(state.position, state.industry, selected, localSide);
    if (why === null) {
      out.push({
        kind: "mine",
        label: `Mine (${mineableAt(state.industry, selected)} left)`
      });
    } else if (why === "already-mined") {
      out.push({ kind: "mine", label: "Mined this turn", done: true });
    }

    const whyLoad = canLoadBelt(state.position, state.industry, selected, localSide);
    if (whyLoad === null) {
      // Label by where this piece's next unit would actually go.
      const sending = unitAt(state.industry, selected, 0, "in");
      const target = loadTarget(state.position, state.industry, selected, localSide, sending);
      out.push({
        kind: "load-belt",
        label: target?.machine ? "Hand to machine" : "Load onto belt"
      });
    } else if (whyLoad === "already-loaded") {
      out.push({ kind: "load-belt", label: "Handed off this turn", done: true });
    }

    return out;
  };

  const act = (kind: SelectionActionKind, square = core.selected) => {
    if (square === undefined) return;
    const { state, localSide } = core;
    // Digging and handing off wait for your turn (§29 U1). The chips are
    // already drawn dimmed off-turn (`selection.ts`), so this is the double
    // click and anything else that reaches `act` without reading them —
    // `send` answers `false` for a refused command and swallows it.
    const blocked = actRefusal(state, localSide, core.viewing);
    if (blocked) {
      core.setRefusal(REFUSAL[blocked]);
      return;
    }
    core.send({ type: kind, square, owner: localSide });
    core.pulse(square);
  };

  /**
   * A double click makes a piece do its hand-off.
   *
   * Hand-off wins when both are available, rather than the click doing nothing
   * because there was no single obvious action: moving material along is the
   * thing you repeat, and the one worth a shortcut. Digging is still there as
   * the fallback when a pawn has nothing to hand over yet, so a double click
   * on a working miner always does *something* — dig this turn, pass it on the
   * next.
   */
  const doubleClickSquare = (square: number) => {
    if (isGameOver(core.state.status)) return;

    const available = actionsFor(square);
    const chosen = available.find((action) => action.kind === "load-belt") ?? available[0];
    if (!chosen) return;
    core.pending = undefined;
    core.cycle = undefined;
    act(chosen.kind, square);
    core.changed();
  };

  return { actionsFor, actions, act, doubleClickSquare };
}
